import { Bell, Search, Menu, LogOut, ChevronDown, Building2, Loader2 } from 'lucide-react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { tenantsApi } from '../../services/tenantsApi'
import LanguageSwitcher from './LanguageSwitcher'
import DownloadAgentButton from './DownloadAgentButton'

export default function Header({ onToggleMobileSidebar }) {
  const { t } = useTranslation()
  const { user, logout, switchTenant } = useAuth()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isTenantOpen, setIsTenantOpen] = useState(false)
  const [tenants, setTenants] = useState([])
  const [isSwitching, setIsSwitching] = useState(false)
  const menuRef = useRef(null)
  const tenantRef = useRef(null)

  const isGlobal = user?.isGlobalUser === true || user?.IsGlobalUser === true
  const currentTenantId = user?.tenantId || user?.TenantId

  // Buscar lista de tenants para usuários globais
  useEffect(() => {
    if (!isGlobal) return
    tenantsApi.getAll()
      .then((data) => setTenants(Array.isArray(data) ? data : []))
      .catch(() => setTenants([]))
  }, [isGlobal])

  // Fechar menus ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false)
      }
      if (tenantRef.current && !tenantRef.current.contains(event.target)) {
        setIsTenantOpen(false)
      }
    }

    if (isMenuOpen || isTenantOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen, isTenantOpen])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleSwitchTenant = useCallback(async (tenantId) => {
    if (tenantId === currentTenantId || isSwitching) return
    setIsTenantOpen(false)
    setIsSwitching(true)
    try {
      const result = await switchTenant(tenantId)
      if (result.success) {
        // Recarregar a página para aplicar o novo contexto de tenant do zero
        window.location.href = '/'
      }
    } finally {
      setIsSwitching(false)
    }
  }, [currentTenantId, isSwitching, switchTenant])

  const getInitials = (name) => {
    if (!name) return 'U'
    const parts = name.trim().split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const profilePhotoBase64 = user?.profilePhotoBase64 || user?.ProfilePhotoBase64

  const currentTenantName = tenants.find(
    (t) => (t.id || t.Id) === currentTenantId
  )?.name || tenants.find(
    (t) => (t.id || t.Id) === currentTenantId
  )?.Name || ''

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
        {/* Mobile menu button */}
        <button
          type="button"
          onClick={onToggleMobileSidebar}
          className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-theme-md"
          aria-label={t('layout.header.openSideMenu')}
        >
          <Menu className="w-6 h-6" />
        </button>

        {/* Search */}
        <div className="flex-1 max-w-lg mx-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('layout.header.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-theme-md text-sm focus:border-theme-primary focus:ring-2 focus:ring-theme-primary/30 focus:outline-none"
            />
          </div>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-3">
          {/* Notifications */}
          <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-theme-md transition-colors">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-error-500 rounded-full"></span>
          </button>

          {/* Tenant switcher — apenas para usuários globais */}
          {isGlobal && tenants.length > 0 && (
            <div className="relative" ref={tenantRef}>
              <button
                onClick={() => setIsTenantOpen(!isTenantOpen)}
                disabled={isSwitching}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-theme-primary bg-theme-primary/10 border border-theme-primary/30 hover:bg-theme-primary/20 rounded-theme-md transition-colors disabled:opacity-60"
                title={t('layout.header.switchTenant')}
              >
                {isSwitching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Building2 className="w-4 h-4" />
                )}
                <span className="hidden sm:block max-w-[140px] truncate">
                  {currentTenantName || t('layout.header.tenantFallback')}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isTenantOpen ? 'rotate-180' : ''}`} />
              </button>

              {isTenantOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-theme-md shadow-lg border border-gray-200 py-1 z-50 max-h-72 overflow-y-auto">
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100 mb-1">
                    {t('layout.header.selectTenant')}
                  </div>
                  {tenants
                    .slice()
                    .sort((a, b) => (a.name || a.Name || '').localeCompare(b.name || b.Name || ''))
                    .map((tenant) => {
                      const id = tenant.id || tenant.Id
                      const name = tenant.name || tenant.Name || id
                      const isActive = id === currentTenantId
                      return (
                        <button
                          key={id}
                          onClick={() => handleSwitchTenant(id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                            isActive
                              ? 'bg-theme-primary/10 text-theme-primary font-medium cursor-default'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <Building2 className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-theme-primary' : 'text-gray-400'}`} />
                          <span className="truncate">{name}</span>
                          {isActive && (
                            <span className="ml-auto w-2 h-2 rounded-full bg-theme-primary flex-shrink-0" />
                          )}
                        </button>
                      )
                    })}
                </div>
              )}
            </div>
          )}

          {/* Language switcher */}
          <LanguageSwitcher />

          {/* Download Connect.IO */}
          <DownloadAgentButton />

          {/* User menu */}
          <div className="relative" ref={menuRef}>
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex items-center gap-2 p-1.5 pr-3 hover:bg-gray-100 rounded-theme-md transition-colors"
            >
              {profilePhotoBase64 ? (
                <div className="avatar avatar-bordered w-8 h-8 bg-white">
                  <img
                    src={profilePhotoBase64}
                    alt={user?.name || t('layout.header.userFallback')}
                  />
                </div>
              ) : (
                <div className="avatar avatar-gradient avatar-bordered w-8 h-8 text-sm font-semibold">
                  {getInitials(user?.name)}
                </div>
              )}
              <span className="text-sm font-medium text-gray-700 hidden sm:block">
                {user?.name || t('layout.header.userFallback')}
              </span>
            </button>

            {/* Dropdown menu */}
            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-theme-md shadow-lg border border-gray-200 py-1 z-50">
                <div className="px-4 py-2 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  {t('layout.header.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
